import {
  CSSProperties,
  ChangeEventHandler,
  FC,
  MouseEventHandler,
  ReactNode,
  RefObject,
  useEffect,
  useRef,
  useState,
} from 'react';
import Cropper from 'react-easy-crop';

import { Camera, CameraResultType } from '@capacitor/camera';
import * as Sentry from '@sentry/browser';

import { useDevice } from '@hooks/useDevice';

import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';

import { ImageUploadCropPreview } from './ImageUploadCropPreview';

const IMAGE_FORMATS = ['x-png', 'jpeg', 'gif', 'png', 'jpg', 'webp', 'heic'];

interface ImageUploadFieldProps {
  inputRef?: RefObject<HTMLInputElement>;
  name?: string;
  cropAspectRatio?: number;
  placeholder: string | ReactNode;
  defaultValue?: string;
  onChange: (imageString: string | ArrayBuffer, blob?: Blob) => void;
  style?: CSSProperties;
  imgStyle?: CSSProperties;
  placeholderStyle?: CSSProperties;
  py?: string;
}

const NATIVE_IMAGE_ENABLED = true;

const adjustImageOrientation = async (file: File) => {
  return new Promise<Blob>((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      // Set canvas dimensions to match the image dimensions
      canvas.width = img.width;
      canvas.height = img.height;

      // Draw the image on the canvas at full size
      ctx.drawImage(img, 0, 0, img.width, img.height);
      canvas.toBlob(resolve, 'image/jpeg');
    };
    img.onerror = (err) => {
      Sentry.captureException(err);
      resolve(file);
    };
    img.src = URL.createObjectURL(file);
  });
};

const readFile = (file: Blob): Promise<string | ArrayBuffer> => {
  return new Promise<string | ArrayBuffer>((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);

    reader.onload = () => {
      resolve(reader.result);
    };

    reader.onerror = (error) => {
      Sentry.captureException(error);
      console.log('Error: ', error);
      reject(error);
    };
  });
};

export const ImageUploadField: FC<ImageUploadFieldProps> = ({
  inputRef: externalInputRef,
  name,
  cropAspectRatio,
  placeholder,
  defaultValue,
  onChange,
  style,
  imgStyle,
  placeholderStyle,
  py = 'py-5',
}) => {
  const { isNativePlatform } = useDevice();
  const internalInputRef = useRef<HTMLInputElement>(null);
  const inputRef = externalInputRef ?? internalInputRef;
  const [selectedFile, setSelectedFile] = useState<Blob | MediaSource>();
  const [previewUrl, setPreviewUrl] = useState<string>(defaultValue);

  // create a preview as a side effect, whenever selected file is changed
  useEffect(() => {
    if (!selectedFile) {
      return;
    }

    const objectUrl = URL.createObjectURL(selectedFile);
    setPreviewUrl(objectUrl);

    // free memory when ever this component is unmounted
    return () => URL.revokeObjectURL(objectUrl);
  }, [selectedFile]);

  const handleCropChange = async (croppedBlob: Blob) => {
    const base64String = await readFile(croppedBlob);
    onChange(base64String, croppedBlob);
  };

  const handleClick: MouseEventHandler<HTMLDivElement> = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (
      // isNativePlatform() &&
      NATIVE_IMAGE_ENABLED
    ) {
      try {
        // const image = await Camera.getPhoto({
        //   quality: 90,
        //   allowEditing: true,
        //   resultType: CameraResultType.Uri, // more efficient than Base64
        // });

        const images = await Camera.pickImages({
          quality: 90,
          limit: 1,
        });

        const response = await fetch(images.photos[0].webPath);
        const blob = await response.blob();
        const base64String = await readFile(blob);

        setSelectedFile(blob);
        onChange(base64String, blob);
      } catch (error) {
        // inputRef.current?.click();
      }
    } else {
      inputRef.current?.click();
    }
  };

  const handleChangeFile: ChangeEventHandler<HTMLInputElement> = async (e) => {
    if (!e.target.files || e.target.files.length === 0) {
      setSelectedFile(undefined);
      return;
    }

    const firstFile = e.target.files[0];

    setSelectedFile(firstFile);

    try {
      const orientedImageBlob = await adjustImageOrientation(firstFile);
      const blob = await readFile(orientedImageBlob);
      onChange(blob);
    } catch (err) {
      console.log('Error reading oriented file, trying original file');
      try {
        const blob = await readFile(firstFile);
        onChange(blob);
      } catch (nextError) {
        console.log('Error reading original file');
      }
    }
  };

  return (
    <div onClick={handleClick} style={{ minHeight: 32, position: 'relative', ...style }}>
      {previewUrl ? (
        <>
          {cropAspectRatio ? (
            <ImageUploadCropPreview
              previewUrl={previewUrl}
              aspectRatio={cropAspectRatio}
              onChange={handleCropChange}
            />
          ) : (
            <img className="d-block" src={previewUrl} style={imgStyle} />
          )}
        </>
      ) : (
        <div
          className={`d-flex flex-column align-items-center ${py} px-2`}
          style={placeholderStyle}
        >
          <div className="fs-1">
            <FontAwesomeIcon name="image" />
          </div>
          <span style={{ textAlign: 'center' }}>{placeholder}</span>
        </div>
      )}
      <div className="d-none">
        <input
          ref={inputRef}
          type="file"
          name={name}
          accept={IMAGE_FORMATS.map((ext) => `image/${ext}`).join(',')}
          onChange={handleChangeFile}
        />
      </div>
    </div>
  );
};
